import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    IconButton,
    Button,
    Chip,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Tooltip
} from '@mui/material';
import { ArrowBack, CheckCircle, Edit, Add } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import TitleBar from '../../components/TitleBar/TitleBar';
import AddQuestionDialog from '../../components/AddQuestionDialog/AddQuestionDialog';
import EditQuestionDialog from '../../components/EditQuestionDialog/EditQuestionDialog';
import { getQuestionsByDefinitionAndEntityType } from '../../services/api';

interface ReviewDimension {
    id: number;
    name: string;
    description: string;
}

interface QuestionData {
    id: number;
    review_question_text: string;
    description: string;
    answer_type: string;
    answer_options: string[];
    review_question_type: string;
    action_required: string;
    default_value: string;
    ideal_value: string;
    review_dimension: ReviewDimension;
    review_definition: any;
    review_entity_type: any;
}

const ViewQuestionnaire = () => {
    const navigate = useNavigate();
    const { reviewDefinitionId, reviewEntityTypeId } = useParams<{
        reviewDefinitionId: string;
        reviewEntityTypeId: string;
    }>();

    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [dimensions, setDimensions] = useState<ReviewDimension[]>([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState<QuestionData | null>(null);
    const [reviewDefinition, setReviewDefinition] = useState<any>(null);
    const [reviewEntityType, setReviewEntityType] = useState<any>(null);

    const fetchQuestions = useCallback(async () => {
        if (!reviewDefinitionId || !reviewEntityTypeId) {
            setError('Invalid questionnaire parameters');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await getQuestionsByDefinitionAndEntityType(
                parseInt(reviewDefinitionId),
                parseInt(reviewEntityTypeId)
            );
            
            // Cast the response data to QuestionData type since API returns full objects
            const questionData = response.data as any as QuestionData[];
            setQuestions(questionData);

            // Extract unique dimensions and sort alphabetically by name
            const uniqueDimensions = Array.from(
                new Map(
                    questionData.map(q => [q.review_dimension.id, q.review_dimension])
                ).values()
            ).sort((a, b) => a.name.localeCompare(b.name));
            setDimensions(uniqueDimensions);
            
            // Set review definition and entity type from first question
            if (questionData.length > 0) {
                setReviewDefinition(questionData[0].review_definition);
                setReviewEntityType(questionData[0].review_entity_type);
            }
            
            setLoading(false);
        } catch (err) {
            setError('Failed to load questionnaire');
            setLoading(false);
        }
    }, [reviewDefinitionId, reviewEntityTypeId]);

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);

    const handleQuestionAdded = () => {
        fetchQuestions();
    };

    const handleEditClick = (question: QuestionData) => {
        setSelectedQuestion(question);
        setEditDialogOpen(true);
    };

    const handleQuestionUpdated = () => {
        fetchQuestions();
        setSelectedQuestion(null);
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setSelectedTab(newValue);
    };

    const getQuestionsByDimension = (dimensionId: number) => {
        return questions
            .filter(q => q.review_dimension.id === dimensionId)
            .sort((a, b) => a.id - b.id);
    };

    const getQuestionTypeIcon = (type: string) => {
        if (type === 'PRODUCT_DEFAULT') {
            return (
                <Tooltip title="Product Default">
                    <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                </Tooltip>
            );
        } else {
            return (
                <Tooltip title="Custom">
                    <Edit sx={{ color: '#ff9800', fontSize: 20 }} />
                </Tooltip>
            );
        }
    };

    if (loading) {
        return (
            <Box>
                <TitleBar />
                <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Container>
            </Box>
        );
    }

    if (error || questions.length === 0) {
        return (
            <Box>
                <TitleBar />
                <Container maxWidth="xl" sx={{ mt: 4 }}>
                    <Alert severity="error">{error || 'No questions found for this questionnaire'}</Alert>
                </Container>
            </Box>
        );
    }

    return (
        <Box>
            <TitleBar />
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconButton
                        aria-label="Back"
                        onClick={() => navigate('/questionnaire-management')}
                        sx={{
                            border: '1px solid #ccc',
                            borderRadius: 2,
                            p: 1,
                            backgroundColor: '#f5f5f5',
                            '&:hover': {
                                backgroundColor: '#e0e0e0',
                            },
                            mr: 2,
                        }}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h4">View Questionnaire</Typography>
                </Box>

                {/* Review Definition and Entity Type Details */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" color="text.secondary">
                            Questionnaire Details
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => setAddDialogOpen(true)}
                            size="small"
                        >
                            Add Question
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: '300px' }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Review Definition
                            </Typography>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {reviewDefinition?.display_label}
                            </Typography>
                            {reviewDefinition?.description && (
                                <Typography variant="body2" color="text.secondary">
                                    {reviewDefinition.description}
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <Chip
                                    label={reviewDefinition?.review_frequency?.display_label || 'N/A'}
                                    size="small"
                                    color="secondary"
                                />
                                <Chip
                                    label={reviewDefinition?.review_type?.display_label || 'N/A'}
                                    size="small"
                                    color="primary"
                                />
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: '300px' }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Review Entity Type
                            </Typography>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {reviewEntityType?.display_label}
                            </Typography>
                            {reviewEntityType?.description && (
                                <Typography variant="body2" color="text.secondary">
                                    {reviewEntityType.description}
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <Chip
                                    label={reviewEntityType?.category || 'N/A'}
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                        <Typography variant="body2" color="text.secondary">
                            Total Questions: <strong>{questions.length}</strong>
                        </Typography>
                    </Box>
                </Paper>

                {/* Questions organized by Dimension in Tabs */}
                <Paper sx={{ p: 0 }}>
                    <Tabs
                        value={selectedTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            px: 2,
                            pt: 2
                        }}
                    >
                        {dimensions.map((dimension, index) => (
                            <Tab
                                key={dimension.id}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <span>{dimension.name}</span>
                                        <Chip
                                            label={getQuestionsByDimension(dimension.id).length}
                                            size="small"
                                            sx={{ height: 20, fontSize: '0.75rem' }}
                                        />
                                    </Box>
                                }
                            />
                        ))}
                    </Tabs>

                    <Box sx={{ p: 3 }}>
                        {dimensions.map((dimension, index) => (
                            <Box
                                key={dimension.id}
                                role="tabpanel"
                                hidden={selectedTab !== index}
                            >
                                {selectedTab === index && (
                                    <>
                                        <TableContainer>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell width="40px"></TableCell>
                                                        <TableCell><strong>Question</strong></TableCell>
                                                        <TableCell>
                                                            <Tooltip title="Description">
                                                                <strong>Desc.</strong>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell width="80px">
                                                            <Tooltip title="Answer Type">
                                                                <strong>Type</strong>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Tooltip title="Answer Options">
                                                                <strong>Options</strong>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Tooltip title="Action Required">
                                                                <strong>Required</strong>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell width="80px">
                                                            <Tooltip title="Default Value">
                                                                <strong>Default</strong>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell width="80px">
                                                            <Tooltip title="Ideal Value">
                                                                <strong>Ideal</strong>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell width="60px"><strong>Actions</strong></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {getQuestionsByDimension(dimension.id).map((question) => (
                                                        <TableRow key={question.id} hover>
                                                            <TableCell>
                                                                {getQuestionTypeIcon(question.review_question_type)}
                                                            </TableCell>
                                                            <TableCell>{question.review_question_text}</TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {question.description || '-'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={question.answer_type}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                {question.answer_options && question.answer_options.length > 0 ? (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                        {question.answer_options.map((option, idx) => (
                                                                            <Chip
                                                                                key={idx}
                                                                                label={option}
                                                                                size="small"
                                                                                sx={{ fontSize: '0.7rem' }}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                ) : (
                                                                    '-'
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {question.action_required || '-'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">
                                                                    {question.default_value || '-'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {question.ideal_value || '-'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Tooltip title="Edit Question">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleEditClick(question)}
                                                                        color="primary"
                                                                    >
                                                                        <Edit fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Paper>
            </Container>

            {/* Add Question Dialog */}
            <AddQuestionDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                onSuccess={handleQuestionAdded}
                defaultReviewDefinitionId={reviewDefinitionId ? parseInt(reviewDefinitionId) : undefined}
                defaultReviewEntityTypeId={reviewEntityTypeId ? parseInt(reviewEntityTypeId) : undefined}
                reviewDefinitionName={reviewDefinition?.display_label}
                reviewEntityTypeName={reviewEntityType?.name}
                reviewEntityTypeDisplayLabel={reviewEntityType?.display_label}
            />

            {/* Edit Question Dialog */}
            <EditQuestionDialog
                open={editDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false);
                    setSelectedQuestion(null);
                }}
                onSuccess={handleQuestionUpdated}
                question={selectedQuestion}
                reviewDefinitionName={reviewDefinition?.display_label}
                reviewEntityTypeDisplayLabel={reviewEntityType?.display_label}
            />
        </Box>
    );
};

export default ViewQuestionnaire;
